// client/src/components/pages/LexiStar/PlanDispatch/planLogic.ts
//
// Pure logic for the plan & dispatch grid (LS-09 §5.8; FR-TRIP-001/003/004/005): draft rows,
// paste-from-spreadsheet parsing, inline validation, the POST /trips:batch payload, and the
// driver-message preview.
//
// The driver (ST-00198): a row's driver comes from the M3 driver register, never free text.
// By default it is the driver allocated to the row's truck at its planned start (GET
// /trips:resolveDriver, useAllocatedDrivers); the planner may pick another driver ("chosen") or
// "No driver"; a pasted name is matched to the register (tag, then name; case-insensitive, whole
// text) and, when it matches nobody, kept as the trip's driver_tag with a warning. The server
// applies the same rule on save (trips service resolveTripDriver), so the payload only names a
// driver the planner chose. The message channel is not planned per trip either: it comes from the
// driver's register entry (preferred channel, opt-ins), as the notifications evaluator's
// @lexistar/messaging planDelivery picks it — driverChannel() mirrors that for display.
//
// Validation here is what the browser can know (FR-TRIP-004 subset: asset under maintenance,
// overlapping trips for the asset or the driver, missing fields, unapproved route). Licence /
// PrDP / document expiry come back from the API as `warnings` on the created trip — shown on the
// row too. Converted to TypeScript in ST-00198 (CLAUDE.md rule).

import type {
  DriverChannel,
  RegisterDriverRow,
  ResolvedTripDriver,
  TripWarning,
} from '../../../../types/lexistar-trip-drivers';
import type {
  AllocationState,
  DriverContact,
  EffectiveDriver,
  PlanAsset,
  PlanLane,
  PlanRoute,
  PlanRow,
  PlanTrip,
  RowValidation,
} from './planTypes';

/** The default driver-message chain (@lexistar/messaging DEFAULT_CHANNEL_ORDER). */
export const CHANNELS: Array<{ value: DriverChannel; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' },
];

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  sms: 'SMS',
  fake: 'Simulator',
};

export const channelLabel = (channel: string | null | undefined): string =>
  channel ? CHANNEL_LABELS[channel] || channel : '';

export const OVERLAP_WINDOW_MS = 2 * 3600 * 1000;

/** The picker's value for "No driver" (sent as driver_id: null). */
export const NO_DRIVER = '__none__';

/** The row note of a pasted channel (ignored: the driver register decides the channel). */
export const CHANNEL_NOTE = 'Channel comes from the driver register';

export const NO_DRIVER_WARNING = 'No driver';

/** The server's warning for a name that matches nobody (trips service notInRegister). */
export const notInRegister = (text: string): string =>
  `Driver '${text}' is not in the register — the allocated driver (if any) receives the messages`;

/** The server's warning for a name that matches several drivers. */
export const ambiguousDriver = (text: string, count: number): string =>
  `Driver '${text}' matches ${count} drivers in the register — choose one; the allocated driver (if any) receives the messages`;

/** A pasted name while the register is not loaded: the server matches it when the plan is saved. */
export const matchedOnSave = (text: string): string =>
  `Driver '${text}' is matched to the driver register when the plan is saved`;

/** Upper-case letters and digits only (mirrors lexistar.normalise_registration()). */
export const normaliseRegistration = (s: unknown): string =>
  String(s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const numOrNull = (v: unknown): number | null =>
  v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);

let seq = 0;
export const newDraftRow = (over: Partial<PlanRow> = {}): PlanRow => ({
  key: `draft-${Date.now()}-${(seq += 1)}`,
  id: null,
  reference: null,
  saved: false,
  asset_id: '',
  trailer_ids: [],
  driver_choice: '',
  driver_text: '',
  driver_id: null,
  driver_name: '',
  driver_tag: null,
  driver_source: null,
  route_id: '',
  material: '',
  order_ref: '',
  planned_tonnes: '',
  planned_start: null,
  budget_l: null,
  override_l: '',
  override_reason: '',
  server_warnings: [],
  row_errors: [],
  row_notes: [],
  ...over,
});

/** A warning as the API sends it ({code, message}; older servers: a string) → its text. */
export const warningText = (w: TripWarning | string): string =>
  typeof w === 'string' ? w : w.message;

/** A saved PLANNED trip (GET /trips, POST /trips:batch) as a read-only grid row. */
export function rowFromTrip(trip: PlanTrip): PlanRow {
  const legs = trip.legs || [];
  const loaded = legs.find((l) => l.kind === 'loaded') || legs[0] || {};
  const tag = trip.driver_tag || null;
  const source =
    trip.driver_source !== undefined
      ? trip.driver_source
      : trip.driver_id
        ? 'chosen'
        : tag
          ? 'tag'
          : null;
  const warnings = (trip.warnings || []).map(warningText);
  // A typed name that matched nobody: the allocated driver gets the messages (also on reload).
  if (source === 'tag' && tag && !warnings.some((w) => w.startsWith(`Driver '${tag}'`))) {
    warnings.push(notInRegister(tag));
  }
  return newDraftRow({
    key: `trip-${trip.id}`,
    id: trip.id,
    reference: trip.reference ?? null,
    saved: true,
    asset_id: trip.asset_id || '',
    registration:
      trip.registration ||
      trip.combination?.horse ||
      String(trip.asset_key || '').replace(/^(reg|asset):/, ''),
    trailers: trip.combination?.trailers || [],
    driver_id: trip.driver_id ?? null,
    driver_name: trip.driver_name || '',
    driver_tag: tag,
    driver_source: source,
    route_id: loaded.route_id || trip.loaded_route_id || '',
    from_name: loaded.from_name || trip.source_name,
    to_name: loaded.to_name || trip.destination_name,
    material: trip.load?.material || '',
    order_ref: trip.load?.order_ref || '',
    planned_tonnes: trip.load?.planned_tonnes ?? '',
    planned_start: trip.planned_start ? new Date(trip.planned_start) : null,
    budget_l: numOrNull(trip.budget_original_l),
    planned_km: numOrNull(trip.planned_km ?? loaded.planned_km),
    server_warnings: warnings,
  });
}

// ── Drivers ─────────────────────────────────────────────────────────────────

/** The whole-text key a name or tag is matched on (the trips service's driverTextKey). */
export const driverTextKey = (s: unknown): string =>
  String(s ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Active drivers whose tag — else, when no tag matches, whose name — is `text` (case-insensitive,
 * whole text), as the server matches a spreadsheet name. One result = a match.
 */
export function matchDrivers(text: string, drivers: RegisterDriverRow[]): RegisterDriverRow[] {
  const key = driverTextKey(text);
  if (!key) return [];
  const active = drivers.filter((d) => d.active !== false);
  const byTag = active.filter((d) => d.tag && driverTextKey(d.tag) === key);
  return byTag.length ? byTag : active.filter((d) => driverTextKey(d.name) === key);
}

export const contactOfRegister = (d: RegisterDriverRow): DriverContact => ({
  id: d.id,
  name: d.name,
  tag: d.tag,
  language: d.language || 'en',
  preferred_channel: d.preferred_channel,
  consents: d.consents || {},
  has_phone: !!d.phone_e164,
  has_telegram: !!d.telegram_chat_id,
});

export const contactOfResolved = (r: ResolvedTripDriver): DriverContact => ({
  id: r.driver_id,
  name: r.driver_name,
  tag: r.driver_tag,
  language: r.language || 'en',
  preferred_channel: r.preferred_channel,
  consents: r.consents || {},
  has_phone: r.has_phone,
  has_telegram: r.has_telegram,
});

/** The picker's label of a register driver. */
export const driverOptionLabel = (d: Pick<RegisterDriverRow, 'name' | 'tag'>): string =>
  d.tag ? `${d.name} · ${d.tag}` : d.name;

/** A saved trip's driver as the server decided it. */
export function savedDriver(
  row: PlanRow,
  driversById: Map<string, RegisterDriverRow> = new Map()
): EffectiveDriver {
  if (row.driver_id) {
    const d = driversById.get(row.driver_id);
    return {
      kind: row.driver_source === 'allocation' ? 'allocation' : 'chosen',
      driverId: row.driver_id,
      name: row.driver_name || d?.name || row.driver_tag || 'Driver',
      tag: row.driver_tag,
      contact: d ? contactOfRegister(d) : null,
    };
  }
  const text = (row.driver_tag || '').trim();
  return text ? { kind: 'text', text, matches: 0 } : { kind: 'unresolved' };
}

export interface DriverContext {
  driversById?: Map<string, RegisterDriverRow>;
  /**
   * The register as loaded (a pasted name is matched against it); absent while it loads or when
   * it could not be loaded — a pasted name is then left for the server to match on save.
   */
  drivers?: RegisterDriverRow[];
  /** The allocation lookup of the row's truck at its planned start; absent = not looked up. */
  allocation?: AllocationState;
}

/** Who a row's trip goes to (see EffectiveDriver in planTypes). */
export function effectiveDriver(row: PlanRow, ctx: DriverContext = {}): EffectiveDriver {
  const driversById = ctx.driversById || new Map<string, RegisterDriverRow>();
  if (row.saved) return savedDriver(row, driversById);
  if (row.driver_choice === NO_DRIVER) return { kind: 'none' };
  if (row.driver_choice) {
    const d = driversById.get(row.driver_choice);
    return {
      kind: 'chosen',
      driverId: row.driver_choice,
      name: d?.name || 'Driver',
      tag: d?.tag ?? null,
      contact: d ? contactOfRegister(d) : null,
    };
  }
  const text = row.driver_text.trim();
  if (text) {
    if (!ctx.drivers) return { kind: 'text', text, matches: 0, unchecked: true };
    const matches = matchDrivers(text, ctx.drivers);
    const [only] = matches;
    if (matches.length === 1 && only) {
      return {
        kind: 'chosen',
        driverId: only.id,
        name: only.name,
        tag: only.tag,
        contact: contactOfRegister(only),
        matchedFrom: text,
      };
    }
    return { kind: 'text', text, matches: matches.length };
  }
  if (!row.asset_id || !ctx.allocation) return { kind: 'unresolved' };
  if (ctx.allocation.status === 'loading') return { kind: 'pending' };
  if (ctx.allocation.status === 'done' && ctx.allocation.result.driver_id) {
    const r = ctx.allocation.result;
    return {
      kind: 'allocation',
      driverId: r.driver_id,
      name: r.driver_name,
      tag: r.driver_tag,
      contact: contactOfResolved(r),
    };
  }
  return { kind: 'unresolved' };
}

/** The driver id a row's trip goes to, when there is one. */
export const driverIdOf = (d: EffectiveDriver): string | null =>
  d.kind === 'chosen' || d.kind === 'allocation' ? d.driverId : null;

// ── Channel (from the driver register) ──────────────────────────────────────

const OPT_IN_REQUIRED: DriverChannel[] = ['whatsapp', 'sms'];
const DRIVER_CHANNELS: DriverChannel[] = ['whatsapp', 'telegram', 'sms', 'fake'];

export type ChannelBlock = 'no_opt_in' | 'opted_out' | 'no_address';

export interface ChannelPlan {
  /** The register's preferred channel. */
  preferred: DriverChannel | null;
  /** The first channel the message can go on; null = none reaches the driver. */
  channel: DriverChannel | null;
  /** Why the preferred channel is passed over (null when it is used, or there is none). */
  preferredBlocked: ChannelBlock | null;
}

/**
 * The channel a driver message goes on, as @lexistar/messaging planDelivery tries them: the
 * preferred channel, then WhatsApp → Telegram → SMS; a channel needs an address (a phone for
 * WhatsApp / SMS, a chat for Telegram), WhatsApp and SMS a current opt-in, and an opt-out
 * rules any out. (Template approval and budgets are the server's to know.)
 */
export function driverChannel(c: DriverContact): ChannelPlan {
  const preferred =
    c.preferred_channel && DRIVER_CHANNELS.includes(c.preferred_channel)
      ? c.preferred_channel
      : null;
  const order = [
    ...(preferred ? [preferred] : []),
    ...CHANNELS.map((x) => x.value).filter((x) => x !== preferred),
  ];
  const blockOf = (channel: DriverChannel): ChannelBlock | null => {
    const address =
      channel === 'telegram' ? c.has_telegram : channel === 'fake' ? true : c.has_phone;
    if (!address) return 'no_address';
    const state = c.consents[channel]?.state;
    if (state === 'opted_out') return 'opted_out';
    if (OPT_IN_REQUIRED.includes(channel) && state !== 'opted_in') return 'no_opt_in';
    return null;
  };
  return {
    preferred,
    channel: order.find((ch) => blockOf(ch) === null) ?? null,
    preferredBlocked: preferred ? blockOf(preferred) : null,
  };
}

/** "no opt-in on WhatsApp", "opted out of SMS", "no phone number for WhatsApp", … */
export function channelBlockText(channel: DriverChannel, why: ChannelBlock): string {
  const label = channelLabel(channel);
  if (why === 'no_opt_in') return `no opt-in on ${label}`;
  if (why === 'opted_out') return `opted out of ${label}`;
  return channel === 'telegram' ? 'no Telegram chat on record' : `no phone number for ${label}`;
}

// ── Paste from spreadsheet ──────────────────────────────────────────────────

export const PASTE_COLUMNS = [
  'Registration',
  'Trailers',
  'Driver (tag or name)',
  'Route code',
  'Material',
  'Order ref',
  'Tonnes',
  'Planned start (YYYY-MM-DD HH:mm)',
  'Channel (ignored)',
];

const parseStart = (s: string): Date | null => {
  const m = String(s || '')
    .trim()
    .match(/^(\d{4})[-/](\d{2})[-/](\d{2})[ T](\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Tab-separated rows (as copied from Excel / Sheets) in PASTE_COLUMNS order → draft rows. A
 * header row is skipped. Unknown registrations / routes are kept as paste errors on the row so
 * nothing typed is lost. The driver cell is matched to the register as the rows render
 * (effectiveDriver); a channel cell is ignored with a note (the register decides the channel).
 */
export function parsePastedRows(
  text: string,
  { assets = [], routes = [] }: { assets?: PlanAsset[]; routes?: PlanRoute[] } = {}
): PlanRow[] {
  const byReg = new Map(assets.map((a) => [normaliseRegistration(a.registration), a]));
  const byCode = new Map(routes.map((r) => [String(r.code || '').toUpperCase(), r]));
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+$/, ''))
    .filter((l) => l.trim());
  const rows: PlanRow[] = [];
  lines.forEach((line) => {
    const cells = line.split('\t').map((c) => c.trim());
    if (/^registration$/i.test(cells[0] || '')) return;
    const [reg, trailers, driver, routeCode, material, orderRef, tonnes, start, channel] = cells;
    const errors: string[] = [];
    const asset = byReg.get(normaliseRegistration(reg));
    if (reg && !asset) errors.push(`Unknown registration ${reg}`);
    const trailerIds = String(trailers || '')
      .split(/[,/;]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const a = byReg.get(normaliseRegistration(t));
        if (!a) errors.push(`Unknown trailer ${t}`);
        return a?.id;
      })
      .filter((id): id is string => !!id);
    const route = routeCode ? byCode.get(routeCode.toUpperCase()) : null;
    if (routeCode && !route) errors.push(`Unknown route ${routeCode}`);
    const plannedStart = start ? parseStart(start) : null;
    if (start && !plannedStart) errors.push(`Unreadable start "${start}" (use YYYY-MM-DD HH:mm)`);
    rows.push(
      newDraftRow({
        asset_id: asset?.id || '',
        trailer_ids: trailerIds,
        driver_text: driver || '',
        route_id: route?.id || '',
        material: material || '',
        order_ref: orderRef || '',
        planned_tonnes: tonnes && Number.isFinite(Number(tonnes)) ? tonnes : '',
        planned_start: plannedStart,
        row_errors: errors,
        row_notes: channel ? [CHANNEL_NOTE] : [],
      })
    );
  });
  return rows;
}

// ── Validation (FR-TRIP-004 subset) ─────────────────────────────────────────

export interface ValidationContext {
  assetsById?: Map<string, PlanAsset>;
  routesById?: Map<string, PlanRoute>;
  rows?: PlanRow[];
  now?: number;
  /** Each row's driver (the page's effectiveDriver per row); else from the row alone. */
  driverOf?: (row: PlanRow) => EffectiveDriver;
  /**
   * ST-00249: the truck's planned-maintenance / breakdown event over the row's planned start, in
   * words ("In the workshop: Brake service until 14:00"), else null — a warning, never a block.
   */
  workshopOf?: (row: PlanRow) => string | null;
}

export function validateRow(row: PlanRow, ctx: ValidationContext = {}): RowValidation {
  const {
    assetsById = new Map<string, PlanAsset>(),
    routesById = new Map<string, PlanRoute>(),
    rows = [],
    now = Date.now(),
    driverOf = (r: PlanRow) => effectiveDriver(r),
    workshopOf,
  } = ctx;
  const errors = [...(row.row_errors || [])];
  const warnings = [...(row.server_warnings || [])];
  const notes = [...(row.row_notes || [])];
  // Saved trips were validated by the server; only the cross-row checks apply.
  if (!row.saved) {
    if (!row.asset_id) errors.push('Choose an asset');
    if (!row.route_id) errors.push('Choose a route');
    if (!row.planned_start) errors.push('Set the planned start');
  }
  if (
    !row.saved &&
    row.override_l !== '' &&
    row.override_l !== null &&
    row.override_l !== undefined
  ) {
    const l = Number(row.override_l);
    if (!Number.isFinite(l) || l <= 0) errors.push('The budget must be more than 0 L');
    if (!String(row.override_reason || '').trim())
      errors.push('Give a reason for changing the budget');
  }

  const asset = assetsById.get(row.asset_id);
  if (asset && ['maintenance', 'out_of_service'].includes(String(asset.status))) {
    warnings.push('Asset is under maintenance');
  }
  const workshop = workshopOf && row.asset_id && row.planned_start ? workshopOf(row) : null;
  if (workshop) warnings.push(workshop);
  const route = routesById.get(row.route_id);
  if (route && route.status === 'proposed') {
    warnings.push('Unapproved route — deviation checks run at reduced severity');
  }
  if (row.planned_start && new Date(row.planned_start).getTime() < now) {
    warnings.push('Planned start is in the past');
  }
  const driver = driverOf(row);
  if (!row.saved) {
    // "No driver" only when nothing resolves (no choice, no allocation); a pasted name that
    // matches nobody says so instead.
    if (driver.kind === 'text') {
      warnings.push(
        driver.unchecked
          ? matchedOnSave(driver.text)
          : driver.matches > 1
            ? ambiguousDriver(driver.text, driver.matches)
            : notInRegister(driver.text)
      );
    } else if (driver.kind === 'none' || driver.kind === 'unresolved') {
      warnings.push(NO_DRIVER_WARNING);
    }
  }
  if (!row.saved && (row.planned_tonnes === '' || row.planned_tonnes === null)) {
    warnings.push('No planned tonnage');
  }

  if (row.planned_start) {
    const t = new Date(row.planned_start).getTime();
    const clash = (other: PlanRow, same: (x: PlanRow) => boolean): boolean =>
      other.key !== row.key &&
      !!other.planned_start &&
      same(other) &&
      Math.abs(new Date(other.planned_start).getTime() - t) < OVERLAP_WINDOW_MS;
    if (row.asset_id && rows.some((o) => clash(o, (x) => x.asset_id === row.asset_id))) {
      warnings.push('Overlaps another trip for this asset');
    }
    // By the driver the trips go to (register id), never by a typed name.
    const mine = driverIdOf(driver);
    if (mine && rows.some((o) => clash(o, (x) => driverIdOf(driverOf(x)) === mine))) {
      warnings.push('Overlaps another trip for this driver');
    }
  }
  return {
    errors,
    warnings,
    notes,
    status: errors.length ? 'errors' : warnings.length ? 'warnings' : 'ready',
  };
}

// ── API payload (POST /trips:batch → trips[]) ───────────────────────────────

export interface TripPayload {
  asset_id: string;
  asset_key?: string;
  combination?: { horse: string; trailers: string[] };
  /** A chosen driver; null = "No driver"; left out = the truck's allocation (the server's default). */
  driver_id?: string | null;
  /** A pasted name that matched nobody (the server matches it again and keeps it). */
  driver_tag?: string;
  lane_id?: string;
  route_id: string;
  from_location_id?: number | string;
  to_location_id?: number | string;
  load: { material: string | null; order_ref: string | null; planned_tonnes: number | null };
  planned_start?: string;
  budget_override?: { litres: number; reason: string };
}

export interface PayloadContext {
  assetsById?: Map<string, PlanAsset>;
  routesById?: Map<string, PlanRoute>;
  lanesById?: Map<string, PlanLane>;
  /** The row's driver (effectiveDriver); else from the row alone. */
  driver?: EffectiveDriver;
}

export function tripPayload(row: PlanRow, ctx: PayloadContext = {}): TripPayload {
  const {
    assetsById = new Map<string, PlanAsset>(),
    routesById = new Map<string, PlanRoute>(),
    lanesById = new Map<string, PlanLane>(),
  } = ctx;
  const asset = assetsById.get(row.asset_id);
  const route = routesById.get(row.route_id);
  const lane = route ? lanesById.get(route.lane_id) : undefined;
  const trailers = row.trailer_ids
    .map((id) => assetsById.get(id))
    .filter((a): a is PlanAsset => !!a)
    .map((a) => normaliseRegistration(a.registration));
  const driver = ctx.driver || effectiveDriver(row);
  const payload: TripPayload = {
    asset_id: row.asset_id,
    asset_key: asset ? `reg:${normaliseRegistration(asset.registration)}` : undefined,
    combination: asset ? { horse: normaliseRegistration(asset.registration), trailers } : undefined,
    // ST-00198: only a driver the planner chose is sent; without one the server takes the
    // truck's allocation at the planned start (the same default the grid shows).
    driver_id:
      driver.kind === 'chosen' ? driver.driverId : driver.kind === 'none' ? null : undefined,
    driver_tag: driver.kind === 'text' ? driver.text : undefined,
    lane_id: route?.lane_id,
    route_id: row.route_id,
    from_location_id: route?.from_location_id ?? lane?.from_location_id,
    to_location_id: route?.to_location_id ?? lane?.to_location_id,
    load: {
      material: row.material || null,
      order_ref: row.order_ref || null,
      planned_tonnes: row.planned_tonnes === '' ? null : Number(row.planned_tonnes),
    },
    planned_start: row.planned_start ? new Date(row.planned_start).toISOString() : undefined,
  };
  if (row.override_l !== '' && row.override_l !== null && row.override_l !== undefined) {
    payload.budget_override = {
      litres: Number(row.override_l),
      reason: String(row.override_reason).trim(),
    };
  }
  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  ) as unknown as TripPayload;
}

// ── Driver message preview (FR-TRIP-005) ───────────────────────────────────

export const LANGUAGES: Array<{ value: 'en' | 'af'; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'af', label: 'Afrikaans' },
];

/** The fields of a trip the standard wording uses. */
export interface MessageTrip {
  reference?: string | null;
  material?: string | null;
  route_code?: string | null;
  route_name?: string | null;
  from_name?: string | null;
  to_name?: string | null;
  budget_l?: number | string | null;
  planned_start?: Date | string | null;
  order_ref?: string | null;
}

const fmtStart = (d: Date | string | null | undefined): string =>
  d
    ? new Date(d).toLocaleString('en-GB', {
        timeZone: 'Africa/Johannesburg',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '—';

/**
 * The dispatch message content — route name, source and destination, map link, fuel
 * allowance, planned start, instructions — with the acknowledgement request. Final wording
 * comes from the tenant's templates (conversation engine, M3); this is what the dispatcher
 * checks before sending when the template preview is not available.
 */
export function driverMessage(trip: MessageTrip, lang: string = 'en'): string {
  const load = trip.material ? `${trip.material}` : 'the load';
  const route = trip.route_code
    ? `${trip.route_code}${trip.route_name ? ` (${trip.route_name})` : ''}`
    : trip.route_name || '—';
  const litres = trip.budget_l != null ? `${Math.round(Number(trip.budget_l))} L` : '—';
  const start = fmtStart(trip.planned_start);
  if (lang === 'af') {
    return [
      `Nuwe rit ${trip.reference || ''}`.trim(),
      `Laai ${load} by ${trip.from_name || '—'} en lewer by ${trip.to_name || '—'} af.`,
      `Roete: ${route}. Kaart: [kaartskakel]`,
      `Begin: ${start}. Brandstoftoelae: ${litres}.`,
      trip.order_ref ? `Bestelling: ${trip.order_ref}.` : null,
      'Antwoord 1 om te aanvaar.',
    ]
      .filter(Boolean)
      .join('\n');
  }
  return [
    `New trip ${trip.reference || ''}`.trim(),
    `Load ${load} at ${trip.from_name || '—'} and deliver to ${trip.to_name || '—'}.`,
    `Route: ${route}. Map: [map link]`,
    `Start: ${start}. Fuel allowance: ${litres}.`,
    trip.order_ref ? `Order: ${trip.order_ref}.` : null,
    'Reply 1 to accept.',
  ]
    .filter(Boolean)
    .join('\n');
}
